import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sprout, FileCheck2, Microscope, FlaskConical, BarChart3, Zap, TestTube2 } from "lucide-react";
import SEO from "../components/SEO";

const ABOUT_IMG = "https://images.pexels.com/photos/9259964/pexels-photo-9259964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1100";
const VIAL_IMG = "https://images.pexels.com/photos/5921726/pexels-photo-5921726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1300&w=1000";

const PILLARS = [
  { icon: Sprout, title: "Research Integrity", body: "Our work is shaped by one principle: research integrity. The systems we put in place make that principle visible." },
  { icon: ShieldCheck, title: "Batch Traceability", body: "Every release ships with a unique batch identifier so each order can be referenced back to its supporting documentation." },
  { icon: Microscope, title: "Laboratory Standards", body: "All compounds are lyophilized, sealed, and lab-handled. Cold-chain logistics protect compound integrity in transit." },
  { icon: FileCheck2, title: "Documentation & Verification", body: "COA documentation is available on request. Research records are maintained for every release we make." },
];

const APPLICATIONS = [
  { icon: FlaskConical, title: "Cell Culture Studies", body: "Investigate cellular responses and mechanisms in controlled laboratory environments." },
  { icon: BarChart3, title: "Protein Analysis", body: "Study protein interactions, binding affinities, and structural characteristics." },
  { icon: Zap, title: "Receptor Studies", body: "Examine receptor binding, signaling pathways, and molecular mechanisms." },
  { icon: TestTube2, title: "Biochemical Assays", body: "Perform analytical tests to measure peptide activity and interactions." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About LuviaRx"
        description="LuviaRx supplies premium research compounds built on batch traceability, third-party testing, and research integrity. Learn how we operate and what we stand for."
        path="/about"
      />
      <section className="py-16 md:py-24">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-4">About LuviaRx</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] tracking-tight text-charcoal-600 mb-6">
              Premium research compounds, <span className="italic font-medium text-sage-600">without the noise.</span>
            </h1>
            <p className="text-lg text-charcoal-400 leading-relaxed mb-5 max-w-xl">
              LuviaRx provides premium research compounds intended exclusively for laboratory and scientific research environments. Every compound we carry is selected and handled with one principle in mind: research integrity.
            </p>
            <p className="text-charcoal-400 leading-relaxed max-w-xl">
              We are not a supplement brand. We are not a clinic. We are a research-compound supplier built for serious investigators, with handling, sourcing, and documentation standards designed to support that work.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div
              data-testid="about-hero-cluster"
              className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-[#fde6ea] via-cream-100 to-[#eef3e8] shadow-soft border border-charcoal-100/40"
            >
              <span aria-hidden className="absolute -top-16 -left-12 w-64 h-64 rounded-full bg-sage-200/55 blur-3xl" />
              <span aria-hidden className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-[#f9e6d8] blur-3xl" />

              <div className="absolute inset-0 flex items-center justify-center px-6">
                <div className="relative w-full max-w-[360px] flex items-end justify-center">
                  {/* TB-500 — left, rotated, goes up */}
                  <figure className="relative -mr-5 translate-y-5">
                    <img
                      src="/products/tb-500.jpg"
                      alt="TB-500 research compound"
                      className="w-24 h-32 md:w-28 md:h-36 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.30)] ring-1 ring-white/50 animate-vial-up"
                      style={{ "--vial-rot": "-7deg" }}
                      loading="lazy"
                    />
                    <span aria-hidden className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-14 h-2 rounded-full bg-charcoal-600/20 blur-md" />
                  </figure>

                  {/* BPC-157 — center, tallest, goes down */}
                  <figure className="relative z-10 -translate-y-4">
                    <img
                      src="/products/bpc-157.jpg"
                      alt="BPC-157 research compound"
                      className="w-28 h-36 md:w-32 md:h-40 object-cover rounded-2xl shadow-[0_30px_60px_-15px_rgba(60,40,55,0.36)] ring-1 ring-white/55 animate-vial-down"
                      style={{ "--vial-rot": "0deg" }}
                      loading="lazy"
                    />
                    <span aria-hidden className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-2 rounded-full bg-charcoal-600/25 blur-md" />
                  </figure>

                  {/* Tesamorelin — right, rotated, goes up */}
                  <figure className="relative -ml-5 translate-y-3">
                    <img
                      src="/products/tesamorelin.jpg"
                      alt="Tesamorelin research compound"
                      className="w-24 h-32 md:w-28 md:h-36 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.30)] ring-1 ring-white/50 animate-vial-up"
                      style={{ "--vial-rot": "7deg" }}
                      loading="lazy"
                    />
                    <span aria-hidden className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-14 h-2 rounded-full bg-charcoal-600/20 blur-md" />
                  </figure>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sage-50/60">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7 max-w-2xl">
              <p className="overline overline-dark mb-3">How we operate</p>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600">
                Four pillars, applied without exception.
              </h2>
            </div>
            <div
              data-testid="how-we-operate-vials"
              className="lg:col-span-5 relative h-32 md:h-40 hidden sm:flex items-center justify-center lg:justify-end"
            >
              <div className="relative flex items-end gap-3">
                <figure className="relative">
                  <img
                    src="/products/cjc1295-ipa.jpg"
                    alt="CJC-1295 + IPA research compound"
                    className="w-20 h-28 md:w-24 md:h-32 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.28)] ring-1 ring-white/50 animate-vial-up"
                    style={{ "--vial-rot": "-5deg" }}
                    loading="lazy"
                  />
                  <span aria-hidden className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-charcoal-600/20 blur-md" />
                </figure>
                <figure className="relative -ml-4">
                  <img
                    src="/products/ghk-cu.jpg"
                    alt="GHK-Cu research compound"
                    className="w-20 h-28 md:w-24 md:h-32 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.28)] ring-1 ring-white/50 animate-vial-down"
                    style={{ "--vial-rot": "5deg" }}
                    loading="lazy"
                  />
                  <span aria-hidden className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-charcoal-600/20 blur-md" />
                </figure>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="surface-card p-7 md:p-8">
                <div className="w-12 h-12 rounded-2xl bg-sage-100 flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5 text-sage-600" strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-charcoal-600 mb-2">{p.title}</h3>
                <p className="text-charcoal-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="intended-applications-section" className="py-16 md:py-24">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-3">Where these compounds belong</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600">
              Intended research applications.
            </h2>
            <p className="mt-4 text-charcoal-400 leading-relaxed">
              LuviaRx compounds are supplied for the kinds of laboratory work below, and only those. Each card is illustrative of context, not endorsement of any specific outcome.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {APPLICATIONS.map((a) => (
              <div
                key={a.title}
                data-testid={`application-card-${a.title.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-[1.75rem] bg-cream-100 border border-charcoal-100/40 shadow-[0_10px_28px_-18px_rgba(60,40,55,0.18)] hover:shadow-[0_22px_44px_-22px_rgba(60,40,55,0.26)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] p-7 md:p-8"
              >
                <div className="w-12 h-12 rounded-2xl bg-white shadow-soft flex items-center justify-center mb-5">
                  <a.icon className="w-5 h-5 text-sage-600" strokeWidth={1.7} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-charcoal-600 mb-2">{a.title}</h3>
                <p className="text-charcoal-400 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div
              data-testid="compliance-hero-vial"
              className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-[#f6efe5] via-cream-100 to-[#eef3e8] shadow-soft border border-charcoal-100/40"
            >
              {/* Soft radial accents */}
              <span aria-hidden className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-sage-200/55 blur-3xl" />
              <span aria-hidden className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full bg-[#f9e6d8] blur-3xl" />
              {/* Subtle grain */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] mix-blend-multiply pointer-events-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
              />

              {/* Large floating vial — anchored bottom-left, partially out of frame */}
              <figure className="absolute -left-10 sm:-left-14 bottom-[-4%] w-[80%] sm:w-[72%] aspect-[3/4] animate-vial-down" style={{ "--vial-rot": "-9deg" }}>
                <img
                  src="/products/tb-500.jpg"
                  alt="TB-500 research compound"
                  className="w-full h-full object-cover rounded-[1.75rem] shadow-[0_40px_70px_-20px_rgba(60,40,55,0.40)] ring-1 ring-white/55"
                  loading="lazy"
                />
                {/* Soft ground shadow */}
                <span
                  aria-hidden
                  className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-40 h-3 rounded-full bg-charcoal-600/25 blur-lg"
                />
              </figure>

              {/* Small "Research Use Only" chip pinned top-right */}
              <span className="absolute top-6 right-6 z-10 inline-flex items-center gap-2 bg-white/85 backdrop-blur-sm border border-charcoal-100/60 rounded-full px-3 py-1.5 text-[10px] font-bold tracking-[0.22em] uppercase text-charcoal-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#e86f2e]" aria-hidden />
                For Research Only
              </span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline mb-3">Compliance</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600 mb-5">
              Research-use-only, in writing and in practice.
            </h2>
            <p className="text-charcoal-400 leading-relaxed mb-4">
              Every compound on this website is supplied strictly for laboratory and scientific research purposes. None of our compounds are drugs, supplements, food, or cosmetics. They have not been evaluated for safety or efficacy in humans.
            </p>
            <p className="text-charcoal-400 leading-relaxed">
              Customers are responsible for lawful, ethical, and safe handling of compounds, including compliance with all applicable laws, institutional policies, and the storage specifications provided with each batch.
            </p>
            <Link to="/products" className="btn-primary mt-7">
              Browse Compounds <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
